import React, { useEffect, useState, useCallback } from 'react';
import { Monitor, Smartphone, MapPin, Loader2, Trash2, ShieldCheck, LogOut } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../App';
import { Button } from '../ui/button';
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel,
} from '../ui/alert-dialog';

function relativeFrom(iso) {
  if (!iso) return 'recently';
  const now = Date.now();
  const then = new Date(iso).getTime();
  const diffSec = Math.max(1, Math.floor((now - then) / 1000));
  if (diffSec < 60) return 'just now';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)} min ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)} hr${Math.floor(diffSec / 3600) === 1 ? '' : 's'} ago`;
  const days = Math.floor(diffSec / 86400);
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months === 1 ? '' : 's'} ago`;
  return `${Math.floor(months / 12)} yr${Math.floor(months / 12) === 1 ? '' : 's'} ago`;
}

function deviceIcon(label) {
  const l = (label || '').toLowerCase();
  if (l.includes('ios') || l.includes('android')) return Smartphone;
  return Monitor;
}

function DeviceRow({ row, onRevoke, revoking }) {
  const Icon = deviceIcon(row.device);
  return (
    <div
      className="card-surface p-5 flex flex-col sm:flex-row sm:items-center gap-4"
      data-testid={`device-row-${row.device_id.slice(0, 8)}`}
    >
      <div className="w-11 h-11 rounded-full bg-[#F9F6F0] border border-[#E6E4DD] flex items-center justify-center shrink-0">
        <Icon className="h-5 w-5 text-[#1A1A1A]" />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-[#1A1A1A] font-medium text-sm">{row.device}</p>
          {row.is_current && (
            <span
              className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-800 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full"
              data-testid="current-device-pill"
            >
              <ShieldCheck className="h-3 w-3" />
              This device
            </span>
          )}
        </div>
        <p className="text-xs text-stone-500 mt-1 flex items-center gap-1">
          <MapPin className="h-3 w-3 shrink-0" />
          {row.location}
          <span className="mx-1 text-stone-300">·</span>
          <span className="font-mono text-[11px] text-stone-400">{row.ip}</span>
        </p>
        <p className="text-xs text-stone-400 mt-0.5">
          Last active {relativeFrom(row.last_seen)}
          {row.first_seen !== row.last_seen && <> · First seen {relativeFrom(row.first_seen)}</>}
        </p>
      </div>

      {!row.is_current && (
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="border-red-200 text-red-700 hover:bg-red-50 shrink-0"
              disabled={revoking}
              data-testid={`revoke-device-${row.device_id.slice(0, 8)}`}
            >
              {revoking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1" />}
              Revoke
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Revoke this device?</AlertDialogTitle>
              <AlertDialogDescription>
                Any active session on <strong>{row.device}</strong> in {row.location} will be signed out immediately.
                If this device is used again in future, you'll receive a new sign-in alert email.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-red-600 hover:bg-red-700"
                onClick={() => onRevoke(row.device_id)}
                data-testid="confirm-revoke-device"
              >
                Revoke device
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}

export default function RecognisedDevicesCard() {
  const [devices, setDevices] = useState(null); // null = loading
  const [revokingId, setRevokingId] = useState(null);
  const [revokingAll, setRevokingAll] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/auth/devices');
      setDevices(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Unable to load devices');
      setDevices([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleRevoke = async (deviceId) => {
    setRevokingId(deviceId);
    try {
      await api.delete(`/auth/devices/${deviceId}`);
      toast.success('Device revoked');
      setDevices((prev) => (prev || []).filter((d) => d.device_id !== deviceId));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not revoke device');
    } finally {
      setRevokingId(null);
    }
  };

  const handleRevokeAllOthers = async () => {
    setRevokingAll(true);
    try {
      const res = await api.post('/auth/devices/revoke-others');
      const n = res.data?.revoked ?? 0;
      toast.success(
        n === 0
          ? 'No other devices to sign out'
          : `Signed out ${n} other device${n === 1 ? '' : 's'}`
      );
      // Optimistic: keep only the current device in the list.
      setDevices((prev) => (prev || []).filter((d) => d.is_current));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not sign out other devices');
    } finally {
      setRevokingAll(false);
    }
  };

  if (devices === null) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="devices-loading">
        <Loader2 className="h-6 w-6 animate-spin text-stone-400" />
      </div>
    );
  }

  const otherCount = (devices || []).filter((d) => !d.is_current).length;

  return (
    <div className="space-y-4" data-testid="recognised-devices-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-heading text-xl font-medium text-[#1A1A1A] flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-[#D4AF37]" />
            Recognised devices
          </h3>
          <p className="text-sm text-stone-500 mt-1">
            The browsers and devices currently signed in to your Zuriya account. Revoke any row you don't recognise.
          </p>
        </div>

        {otherCount > 0 && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="border-red-200 text-red-700 hover:bg-red-50 shrink-0"
                disabled={revokingAll}
                data-testid="revoke-all-others-btn"
              >
                {revokingAll ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <LogOut className="h-4 w-4 mr-1.5" />
                )}
                Sign out all other devices
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Sign out every other device?</AlertDialogTitle>
                <AlertDialogDescription>
                  All {otherCount} other active session{otherCount === 1 ? '' : 's'} will be immediately
                  signed out. You'll stay signed in on this device. If any of those devices are used
                  again, you'll receive a new sign-in alert email.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-red-600 hover:bg-red-700"
                  onClick={handleRevokeAllOthers}
                  data-testid="confirm-revoke-all-others"
                >
                  Sign out all others
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>

      {devices.length === 0 ? (
        <div className="card-surface p-4 sm:p-8 text-center text-sm text-stone-500" data-testid="devices-empty">
          No recognised devices yet. They'll appear here as you sign in.
        </div>
      ) : (
        <div className="space-y-3">
          {devices.map((row) => (
            <DeviceRow
              key={row.device_id}
              row={row}
              onRevoke={handleRevoke}
              revoking={revokingId === row.device_id}
            />
          ))}
        </div>
      )}
    </div>
  );
}
